import { useTheme } from '@material-ui/core';
import React, { FC, useCallback } from 'react';
import { addMinutes } from 'date-fns';
import { timeOnly, timeZone as formatTimeZone } from '@onwardcare/core';
import { useSessionContext } from '../../contexts/session-context';

type RideEtaDisplayProps = {
  ride: {
    rideCompletionStatus?: string | null;
    currentPickupDriverWaypoint?: {
      etaMinutes?: number | null;
    } | null;
    currentDropoffDriverWaypoint?: {
      etaMinutes?: number | null;
    } | null;
  };
  account?: {
    tzName?: string | null;
  } | null;
  inline?: boolean;
};

export const RideEtaDisplay: FC<RideEtaDisplayProps> = ({ ride, account, inline = false }) => {
  const theme = useTheme();
  const { session } = useSessionContext();
  const timeZone = account?.tzName ?? session?.account?.tzName ?? undefined;

  const rideCompletionStatus = ride.rideCompletionStatus;
  const pickupWaypoint = ride.currentPickupDriverWaypoint;
  const dropoffWaypoint = ride.currentDropoffDriverWaypoint;

  const showPickupEta =
    rideCompletionStatus === 'confirmed' && pickupWaypoint?.etaMinutes;
  const showDropoffEta =
    rideCompletionStatus === 'in-progress' && dropoffWaypoint?.etaMinutes;


  const formatEtaTime = useCallback(
    (etaMinutes: number) => {
      if (!timeZone) {
        return null;
      }

      const now = new Date();
      const etaTime = addMinutes(now, etaMinutes);

      const formattedTime = timeOnly(etaTime, { timeZone, hideTimeZone: true });

      const tzAbbrev = formatTimeZone(etaTime, { timeZone });

      return `${formattedTime} ${tzAbbrev}`;
    },
    [timeZone],
  );

  if (!showPickupEta && !showDropoffEta) {
    return null;
  }

  const labelStyle = {
    color: theme.palette.primary.main,
    fontWeight: 'bold' as const,
  };

  const Container = inline ? 'span' : 'div';

  const renderEta = (label: string, etaMinutes: number) => {
    const formattedTime = formatEtaTime(etaMinutes);
    const timeContent = formattedTime ? (
      <>
        {formattedTime} ({etaMinutes}min)
      </>
    ) : (
      <>{etaMinutes}min</>
    );

    if (inline) {
      return (
        <Container>
          {' '}
          <b style={labelStyle}>{label}</b> {timeContent}
        </Container>
      );
    }

    return (
      <Container>
        <div>
          <b style={labelStyle}>{label}</b>
        </div>
        <div>{timeContent}</div>
      </Container>
    );
  };

  return (
    <>
      {showPickupEta && pickupWaypoint.etaMinutes && (
        renderEta('Pickup ETA:', pickupWaypoint.etaMinutes)
      )}
      {showDropoffEta && dropoffWaypoint.etaMinutes && (
        renderEta('Dropoff ETA:', dropoffWaypoint.etaMinutes)
      )}
    </>
  );
};
